import React, { useState, useCallback, useRef, useEffect } from "react";
import api, { formatVND } from "@/lib/api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useSearchParams } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import TimelineGrid from "@/components/schedule/TimelineGrid";
import BookingModal from "@/components/schedule/BookingModal";
import BookingList from "@/components/schedule/BookingList";
import PinDialog from "@/components/PinDialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarPicker } from "@/components/ui/calendar";
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Users,
  Clock,
  Camera,
  FileSpreadsheet,
  Loader2,
  XCircle,
  Calendar as CalendarIcon,
} from "lucide-react";

const STATUS_COLORS = [
  { key: "confirmed", label: "Đã xác nhận", color: "bg-lime-500" },
  { key: "pending", label: "Chờ xác nhận", color: "bg-amber-500" },
  { key: "completed", label: "Hoàn thành", color: "bg-sky-500" },
  { key: "cancelled", label: "Đã huỷ", color: "bg-red-500" },
  { key: "unpaid", label: "Chưa thanh toán", color: "bg-orange-500" },
  { key: "awaiting", label: "Chờ XN thanh toán", color: "bg-purple-500" },
  { key: "social", label: "SOCIAL", color: "bg-teal-500" },
];

function addDays(dateStr, days) {
  const d = new Date(dateStr + "T00:00:00");
  d.setDate(d.getDate() + days);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function formatDateVN(dateStr) {
  const d = new Date(dateStr + "T00:00:00");
  const dayNames = ["Chủ Nhật", "Thứ Hai", "Thứ Ba", "Thứ Tư", "Thứ Năm", "Thứ Sáu", "Thứ Bảy"];
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dayNames[d.getDay()]}, ${dd}/${mm}`;
}

function shortDay(dateStr) {
  const d = new Date(dateStr + "T00:00:00");
  const abbr = ["CN", "T2", "T3", "T4", "T5", "T6", "T7"];
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return { abbr: abbr[d.getDay()], date: `${dd}/${mm}` };
}

export default function SchedulePage() {
  const today = (() => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  })();
  const [selectedDate, setSelectedDate] = useState(today);
  const queryClient = useQueryClient();
  const [typeFilter, setTypeFilter] = useState("");
  const { user } = useAuth();
  const isEmployee = user?.role === "employee";
  const [searchParams, setSearchParams] = useSearchParams();

  // Booking modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editBooking, setEditBooking] = useState(null);
  const [prefillCourtId, setPrefillCourtId] = useState("");
  const [prefillTime, setPrefillTime] = useState("");
  const [forceBookingType, setForceBookingType] = useState("");

  // PIN dialog state for delete
  const [deletePinOpen, setDeletePinOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);

  // React Query: fetch courts, bookings, stats
  const { data: courtsData } = useQuery({
    queryKey: ["courts"],
    queryFn: () => api.get("/courts").then(r => r.data.courts || []),
  });
  const courts = courtsData || [];

  const { data: bookingsData, isLoading: loading } = useQuery({
    queryKey: ["bookings", selectedDate],
    queryFn: () => api.get(`/bookings?date=${selectedDate}`).then(r => r.data.bookings || []),
  });
  const bookings = bookingsData || [];

  const { data: statsData } = useQuery({
    queryKey: ["stats", selectedDate],
    queryFn: () => api.get(`/bookings/stats?date=${selectedDate}`).then(r => r.data),
  });
  const stats = statsData || { playing_count: 0, tired_count: 0, total_revenue: 0, booking_count: 0 };

  // Handle notification deep-link: read date & booking_id from URL params
  const pendingBookingIdRef = useRef(null);
  const [notifTrigger, setNotifTrigger] = useState(0);

  useEffect(() => {
    const paramDate = searchParams.get("date");
    const paramBookingId = searchParams.get("booking_id");
    if (paramDate) {
      setSelectedDate(paramDate);
      if (paramBookingId) {
        pendingBookingIdRef.current = paramBookingId;
        setNotifTrigger(prev => prev + 1); // Force trigger
      }
      // Clear search params after reading
      setSearchParams({}, { replace: true });
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // When we have a pending booking_id, poll bookings until we find it and open modal
  useEffect(() => {
    if (!pendingBookingIdRef.current) return;
    const targetId = pendingBookingIdRef.current;

    // Try to find immediately in current bookings
    const tryOpen = () => {
      const currentBookings = queryClient.getQueryData(["bookings", selectedDate]);
      if (!currentBookings || !currentBookings.length) return false;
      const found = currentBookings.find(b => b.id === targetId);
      if (found) {
        pendingBookingIdRef.current = null;
        setEditBooking(found);
        setPrefillCourtId("");
        setPrefillTime("");
        setForceBookingType("");
        setModalOpen(true);
        setTimeout(() => {
          const bar = document.querySelector(`[data-testid="booking-bar-${targetId}"]`);
          if (bar) {
            bar.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
            bar.style.boxShadow = "0 0 20px rgba(45,212,191,0.6)";
            setTimeout(() => { bar.style.boxShadow = ""; }, 3000);
          }
        }, 400);
        return true;
      }
      return false;
    };

    // Try immediately
    if (tryOpen()) return;

    // Poll every 200ms for up to 3 seconds (in case bookings are loading)
    let attempts = 0;
    const interval = setInterval(() => {
      attempts++;
      if (tryOpen() || attempts > 15) {
        clearInterval(interval);
        if (attempts > 15) pendingBookingIdRef.current = null;
      }
    }, 200);

    return () => clearInterval(interval);
  }, [notifTrigger, selectedDate, queryClient]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mutations with optimistic updates
  const deleteBookingMutation = useMutation({
    mutationFn: (bookingId) => api.delete(`/bookings/${bookingId}`),
    onMutate: async (bookingId) => {
      await queryClient.cancelQueries({ queryKey: ["bookings", selectedDate] });
      const prev = queryClient.getQueryData(["bookings", selectedDate]);
      queryClient.setQueryData(["bookings", selectedDate], (old) =>
        (old || []).filter(b => b.id !== bookingId)
      );
      return { prev };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(["bookings", selectedDate], ctx.prev);
      toast.error("Lỗi khi xóa đặt sân");
    },
    onSuccess: () => { toast.success("Đã xóa đặt sân"); },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["bookings", selectedDate] });
      queryClient.invalidateQueries({ queryKey: ["stats", selectedDate] });
    },
  });

  const updateBookingMutation = useMutation({
    mutationFn: ({ bookingId, data }) => api.put(`/bookings/${bookingId}`, data),
    onMutate: async ({ bookingId, data }) => {
      await queryClient.cancelQueries({ queryKey: ["bookings", selectedDate] });
      const prev = queryClient.getQueryData(["bookings", selectedDate]);
      queryClient.setQueryData(["bookings", selectedDate], (old) =>
        (old || []).map(b => b.id === bookingId ? { ...b, ...data } : b)
      );
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(["bookings", selectedDate], ctx.prev);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["bookings", selectedDate] });
      queryClient.invalidateQueries({ queryKey: ["stats", selectedDate] });
    },
  });

  const invalidateBookings = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["bookings", selectedDate] });
    queryClient.invalidateQueries({ queryKey: ["stats", selectedDate] });
  }, [queryClient, selectedDate]);

  const handleVerifyPayment = async (bookingId) => {
    try {
      await api.post(`/bookings/${bookingId}/verify-payment`);
      toast.success("Đã xác nhận thanh toán");
      invalidateBookings();
    } catch (err) { toast.error(err.response?.data?.detail || "Lỗi"); }
  };

  const handleUnverifyPayment = async (bookingId) => {
    try {
      await api.post(`/bookings/${bookingId}/unverify-payment`);
      toast.success("Đã hoàn tác xác nhận thanh toán");
      invalidateBookings();
    } catch (err) { toast.error(err.response?.data?.detail || "Lỗi"); }
  };

  // Timeline screenshot capture
  const timelineRef = useRef(null);
  const [capturing, setCapturing] = useState(false);
  const [pdfHideName, setPdfHideName] = useState(false);
  const [pdfExportMode, setPdfExportMode] = useState(false);

  const handleCapturePDF = useCallback(async () => {
    const el = timelineRef.current?.querySelector('[data-testid="timeline-grid"]');
    if (!el) { toast.error("Không tìm thấy timeline"); return; }
    setCapturing(true);
    setPdfExportMode(true);
    toast.info("Đang chụp timeline...");
    await new Promise(r => setTimeout(r, 300));
    try {
      const html2canvas = (await import("html2canvas")).default;
      const { jsPDF } = await import("jspdf");
      const headerScroll = el.querySelector(".hide-scrollbar");
      const bodyScroll = el.querySelectorAll(".overflow-x-auto")[1] || el.querySelector(".overflow-y-auto");
      const bodyInner = bodyScroll?.firstElementChild;
      if (!bodyInner) { toast.error("Lỗi: không tìm thấy nội dung"); setCapturing(false); setPdfExportMode(false); return; }

      // Constants matching TimelineGrid.js
      const HOUR_W = 120;
      const TL_START = 5;
      const SIDE_W = 180;
      const SC = 1.5;

      // Time range: (now - 2h) → (last booking end + 30min)
      const now = new Date();
      const nowMin = now.getHours() * 60 + now.getMinutes();
      const rangeStartMin = Math.max(nowMin - 120, TL_START * 60);
      let rangeEndMin = nowMin + 60;
      if (bookings.length > 0) {
        bookings.forEach(b => {
          if (b.end_time) {
            const [h, m] = b.end_time.split(":").map(Number);
            if (h * 60 + m > rangeEndMin) rangeEndMin = h * 60 + m;
          }
        });
      }
      rangeEndMin = Math.min(rangeEndMin + 30, 24 * 60);
      const pxPerMin = HOUR_W / 60;
      const cropL = (rangeStartMin - TL_START * 60) * pxPerMin;
      const cropR = (rangeEndMin - TL_START * 60) * pxPerMin;

      // Save & expand DOM
      const orig = {
        scroll: bodyScroll.scrollLeft, overflow: bodyScroll.style.overflow,
        w: bodyScroll.style.width, maxH: bodyScroll.style.maxHeight, elMaxH: el.style.maxHeight,
      };
      bodyScroll.scrollLeft = 0;
      bodyScroll.style.overflow = "visible";
      bodyScroll.style.width = `${bodyInner.scrollWidth}px`;
      bodyScroll.style.maxHeight = "none";
      el.style.maxHeight = "none";
      if (headerScroll) { headerScroll.scrollLeft = 0; headerScroll.style.overflow = "visible"; headerScroll.style.width = `${bodyInner.scrollWidth + SIDE_W}px`; }

      // Style booking bar text for PDF
      const bars = el.querySelectorAll("[data-booking-bar]");
      bars.forEach(bar => {
        bar.style.overflow = "visible";
        const tc = bar.querySelector(".pointer-events-none");
        if (tc) {
          tc.style.overflow = "visible";
          Array.from(tc.children).forEach(ch => {
            ch.style.whiteSpace = "normal";
            ch.style.wordBreak = "break-word";
            ch.style.overflow = "visible";
            ch.style.textOverflow = "clip";
            ch.style.maxWidth = "none";
            const txt = ch.textContent || "";
            if (/\d{2}:\d{2}/.test(txt) && !txt.includes("đ")) {
              ch.style.fontWeight = "700";
              ch.style.fontSize = "12px";
            }
          });
        }
      });
      await new Promise(r => setTimeout(r, 200));

      // Phase 1: Capture FULL element
      const fullCanvas = await html2canvas(el, {
        backgroundColor: "#0f172a",
        scale: SC,
        useCORS: true,
        logging: false,
        windowWidth: bodyInner.scrollWidth + SIDE_W + 40,
      });

      // Restore DOM
      bars.forEach(bar => {
        bar.style.overflow = "hidden";
        const tc = bar.querySelector(".pointer-events-none");
        if (tc) {
          tc.style.overflow = "";
          Array.from(tc.children).forEach(ch => {
            ch.style.whiteSpace = ""; ch.style.wordBreak = ""; ch.style.overflow = "";
            ch.style.textOverflow = ""; ch.style.maxWidth = "";
            ch.style.fontWeight = ""; ch.style.fontSize = "";
          });
        }
      });
      bodyScroll.style.overflow = orig.overflow;
      bodyScroll.style.width = orig.w;
      bodyScroll.style.maxHeight = orig.maxH;
      bodyScroll.scrollLeft = orig.scroll;
      el.style.maxHeight = orig.elMaxH;
      if (headerScroll) { headerScroll.style.overflow = ""; headerScroll.style.width = ""; }

      // Phase 2: Crop — sidebar (0→180) + timeline (cropL→cropR)
      const sidebarPx = Math.round(SIDE_W * SC);
      const tlCropL = Math.round((SIDE_W + cropL) * SC);
      const tlCropR = Math.round((SIDE_W + cropR) * SC);
      const tlWidth = tlCropR - tlCropL;
      const outW = sidebarPx + tlWidth;
      const outH = fullCanvas.height;

      const croppedCanvas = document.createElement("canvas");
      croppedCanvas.width = outW;
      croppedCanvas.height = outH;
      const ctx = croppedCanvas.getContext("2d");
      // Draw sidebar
      ctx.drawImage(fullCanvas, 0, 0, sidebarPx, outH, 0, 0, sidebarPx, outH);
      // Draw cropped timeline
      ctx.drawImage(fullCanvas, tlCropL, 0, tlWidth, outH, sidebarPx, 0, tlWidth, outH);

      // Generate PDF
      const imgData = croppedCanvas.toDataURL("image/jpeg", 0.85);
      const pdfW = outW * 0.264583 / SC;
      const pdfH = outH * 0.264583 / SC;
      const pdf = new jsPDF({ orientation: "landscape", unit: "mm", format: [pdfW, pdfH] });
      pdf.addImage(imgData, "JPEG", 0, 0, pdfW, pdfH);
      const dateLabel = formatDateVN(selectedDate).replace(/[,\s/]+/g, "_");
      pdf.save(`Lich_san_${dateLabel}.pdf`);
      toast.success("Đã xuất PDF lịch sân!");
    } catch (err) {
      console.error("PDF capture error:", err);
      toast.error("Lỗi khi chụp timeline");
    } finally {
      setPdfExportMode(false);
      setCapturing(false);
    }
  }, [selectedDate, bookings]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleExportExcel = useCallback(async () => {
    try {
      const response = await api.get(`/export/bookings?date=${selectedDate}&format=excel`, { responseType: "blob" });
      const blob = new Blob([response.data]);
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `booking_${selectedDate}.xlsx`;
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success("Đã xuất Excel");
    } catch { toast.error("Lỗi khi xuất Excel"); }
  }, [selectedDate]);

  // Filtered bookings for timeline — hide social from grid, only show regular courts
  const filteredBookings = typeFilter
    ? bookings.filter((b) => b.booking_type === typeFilter)
    : bookings.filter((b) => b.booking_type !== "social");

  // Booking mới nhất trong ngày đang xem (created_at cao nhất) — để highlight
  const newestBookingId = React.useMemo(() => {
    const active = filteredBookings.filter(b => b.status !== "cancelled" && b.created_at);
    if (!active.length) return null;
    const newest = active.reduce((a, b) => (a.created_at > b.created_at ? a : b));
    return newest.id;
  }, [filteredBookings]);

  // Quick-select days (today + 6)
  const quickDays = Array.from({ length: 7 }, (_, i) => addDays(today, i));

  const handleSlotClick = (courtId, time) => {
    setPrefillCourtId(courtId);
    setPrefillTime(time);
    setEditBooking(null);
    setForceBookingType("");
    setModalOpen(true);
  };

  const handleSocialSlotClick = (courtId, time) => {
    setPrefillCourtId(courtId);
    setPrefillTime(time);
    setEditBooking(null);
    setForceBookingType("social");
    setModalOpen(true);
  };

  const handleBookingClick = (booking) => {
    setEditBooking(booking);
    setPrefillCourtId("");
    setPrefillTime("");
    setForceBookingType("");
    setModalOpen(true);
  };

  const handleDeleteBooking = (bookingId) => {
    setPendingDeleteId(bookingId);
    setDeletePinOpen(true);
  };

  const handleDeleteBookingDirect = (bookingId) => {
    // Called from TimelineGrid after PIN already verified there
    deleteBookingMutation.mutate(bookingId);
  };

  const doDeleteBookingConfirmed = () => {
    if (pendingDeleteId) {
      deleteBookingMutation.mutate(pendingDeleteId);
      setPendingDeleteId(null);
    }
  };

  const handleNewBooking = () => {
    setEditBooking(null);
    setPrefillCourtId("");
    setPrefillTime("");
    setForceBookingType("");
    setModalOpen(true);
  };

  const handleApproveBooking = useCallback(async (bookingId) => {
    try {
      const { data } = await api.post(`/bookings/${bookingId}/approve`);
      toast.success(data.message || "Đã xác nhận");
      invalidateBookings();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lỗi xác nhận");
    }
  }, [invalidateBookings]);

  // Cancel with reason dialog
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelBookingId, setCancelBookingId] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);

  const handleCancelBooking = useCallback((bookingId) => {
    setCancelBookingId(bookingId);
    setCancelReason("");
    setCancelDialogOpen(true);
  }, []);

  const submitCancel = useCallback(async () => {
    if (!cancelReason.trim()) { toast.error("Vui lòng nhập lý do huỷ"); return; }
    setCancelling(true);
    try {
      const { data } = await api.post(`/bookings/${cancelBookingId}/cancel`, { reason: cancelReason });
      toast.success(data.message || "Đã huỷ");
      setCancelDialogOpen(false);
      invalidateBookings();
    } catch (err) { toast.error(err.response?.data?.detail || "Lỗi huỷ"); }
    finally { setCancelling(false); }
  }, [cancelBookingId, cancelReason, invalidateBookings]);

  return (
    <div className="space-y-4 animate-fade-in-up" data-testid="schedule-page">
      {/* A. Header */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          {/* Day nav */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSelectedDate(addDays(selectedDate, -1))}
              className="p-1.5 rounded-md bg-[#1e293b] border border-[#334155] hover:bg-[#334155] text-slate-400 transition-colors"
              data-testid="prev-day-btn"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#1e293b] border border-[#334155] hover:border-teal-500/60 hover:bg-[#334155]/40 transition-colors cursor-pointer"
                  data-testid="date-picker-trigger"
                >
                  <CalendarIcon className="w-3.5 h-3.5 text-teal-400 shrink-0" />
                  <span className="font-heading text-sm sm:text-base md:text-lg font-bold text-[#F8FAFC] whitespace-nowrap" data-testid="current-date-display">
                    {formatDateVN(selectedDate)}
                  </span>
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-[#1e293b] border-[#334155]" align="center" data-testid="date-picker-popover">
                <CalendarPicker
                  mode="single"
                  selected={new Date(selectedDate + "T00:00:00")}
                  onSelect={(d) => {
                    if (!d) return;
                    const yyyy = d.getFullYear();
                    const mm = String(d.getMonth() + 1).padStart(2, "0");
                    const dd = String(d.getDate()).padStart(2, "0");
                    setSelectedDate(`${yyyy}-${mm}-${dd}`);
                  }}
                  initialFocus
                  className="bg-[#1e293b] text-[#F8FAFC]"
                />
              </PopoverContent>
            </Popover>

            <button
              onClick={() => setSelectedDate(addDays(selectedDate, 1))}
              className="p-1.5 rounded-md bg-[#1e293b] border border-[#334155] hover:bg-[#334155] text-slate-400 transition-colors"
              data-testid="next-day-btn"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          {/* Quick select days + new booking button */}
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex gap-1">
              {quickDays.map((d) => {
                const { abbr, date } = shortDay(d);
                const isSelected = d === selectedDate;
                const isToday = d === today;
                return (
                  <button
                    key={d}
                    onClick={() => setSelectedDate(d)}
                    className={`flex flex-col items-center px-2 py-1 rounded-md text-[11px] transition-colors ${
                      isSelected
                        ? "bg-lime-500/20 text-lime-400 border border-lime-500/30"
                        : "bg-[#1e293b] text-slate-400 border border-[#334155] hover:text-slate-200"
                    }`}
                    data-testid={`quick-day-${d}`}
                  >
                    <span className="font-semibold">{isToday ? "Hôm nay" : abbr}</span>
                    <span className="opacity-70">{date}</span>
                  </button>
                );
              })}
            </div>
            {!isEmployee && (
            <Button
              onClick={handleNewBooking}
              className="bg-lime-500 text-[#0f172a] font-semibold hover:bg-lime-400 h-9"
              data-testid="new-booking-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Đặt sân
            </Button>
            )}
            {!isEmployee && (
            <div className="flex items-center gap-1.5">
              <div className="flex items-center gap-2 mr-1">
                <label className="flex items-center gap-1 cursor-pointer" data-testid="toggle-hide-name-pdf">
                  <input type="checkbox" checked={pdfHideName} onChange={(e) => setPdfHideName(e.target.checked)}
                    className="w-3 h-3 rounded border-slate-500 text-lime-500 focus:ring-lime-500/30 bg-[#0f172a]" />
                  <span className="text-[10px] text-slate-400">Ẩn tên KH</span>
                </label>
              </div>
              <button onClick={handleCapturePDF} disabled={capturing}
                className="flex items-center gap-1 text-xs text-slate-400 hover:text-red-400 border border-[#334155] rounded-md px-2.5 py-1.5 transition-colors disabled:opacity-50"
                data-testid="export-pdf-btn">
                {capturing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
                {capturing ? "Đang chụp..." : "PDF"}
              </button>
              <button onClick={handleExportExcel}
                className="flex items-center gap-1 text-xs text-slate-400 hover:text-lime-400 border border-[#334155] rounded-md px-2.5 py-1.5 transition-colors"
                data-testid="export-excel-btn">
                <FileSpreadsheet className="w-3.5 h-3.5" /> Excel
              </button>
            </div>
            )}
          </div>
        </div>

        {/* Stats bar */}
        <div className="flex items-center gap-4 text-sm">
          <div className="flex items-center gap-1.5 text-lime-400" data-testid="stat-playing">
            <Users className="w-4 h-4" />
            <span className="font-medium">{stats.playing_count} khách đang chơi</span>
          </div>
          {!isEmployee && (
            <div className="hidden sm:flex items-center gap-1.5 text-slate-400">
              <span>Tổng: <span className="text-lime-400 font-semibold">{formatVND(stats.total_revenue)}đ</span></span>
            </div>
          )}
        </div>
      </div>

      {/* B. Legend & Filter */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          {STATUS_COLORS.map((s) => (
            <div key={s.key} className="flex items-center gap-1.5">
              <div className={`w-3 h-3 rounded-sm ${s.color}`} />
              <span className="text-[11px] text-slate-400">{s.label}</span>
            </div>
          ))}
        </div>
        <div className="flex gap-1.5">
          {[
            { value: "", label: "Tất cả" },
            { value: "regular", label: "Thường" },
            { value: "social", label: "Social" },
          ].map((f) => (
            <button
              key={f.value}
              onClick={() => setTypeFilter(f.value)}
              className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                typeFilter === f.value
                  ? "bg-lime-500/20 text-lime-400"
                  : "bg-[#1e293b] text-slate-400 hover:text-slate-200 border border-[#334155]"
              }`}
              data-testid={`type-filter-${f.value || "all"}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* C. Timeline Grid */}
      {loading ? (
        <div className="bg-[#1e293b] border border-[#334155] rounded-lg p-8 flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-lime-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div ref={timelineRef}>
          <TimelineGrid
          courts={courts}
          bookings={filteredBookings}
          selectedDate={selectedDate}
          onSlotClick={isEmployee ? undefined : handleSlotClick}
          onSocialSlotClick={isEmployee ? undefined : handleSocialSlotClick}
          onBookingClick={isEmployee ? undefined : handleBookingClick}
          onDeleteBooking={isEmployee ? undefined : handleDeleteBookingDirect}
          onSaved={invalidateBookings}
          updateBookingMutation={isEmployee ? undefined : updateBookingMutation}
          hidePrice={isEmployee || pdfExportMode}
          hideName={pdfExportMode && pdfHideName}
          newestBookingId={pdfExportMode ? null : newestBookingId}
          onVerifyPayment={isEmployee ? undefined : handleVerifyPayment}
          onUnverifyPayment={isEmployee ? undefined : handleUnverifyPayment}
        />
        </div>
      )}

      {/* D. Bottom Panel - Hidden for employee */}
      {!isEmployee && (
      <BookingList
        bookings={bookings}
        onEdit={handleBookingClick}
        onDelete={handleDeleteBooking}
        onApprove={handleApproveBooking}
        onCancel={handleCancelBooking}
        onRefresh={invalidateBookings}
      />
      )}

      {/* Cancel reason dialog */}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent className="bg-[#1e293b] border-[#334155] sm:max-w-sm" data-testid="cancel-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#F8FAFC] font-heading flex items-center gap-2 text-base">
              <XCircle className="w-5 h-5 text-red-400" />
              Huỷ đặt sân
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <p className="text-xs text-slate-400">Nhập lý do huỷ (bắt buộc). Lý do sẽ được gửi thông báo cho khách hàng.</p>
            <Textarea value={cancelReason} onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Nhập lý do huỷ..." className="bg-[#0f172a] border-[#334155] text-slate-200 h-24"
              autoFocus data-testid="cancel-reason-input" />
          </div>
          <DialogFooter className="flex gap-2">
            <Button variant="ghost" onClick={() => setCancelDialogOpen(false)} className="text-slate-400">Đóng</Button>
            <Button onClick={submitCancel} disabled={cancelling || !cancelReason.trim()}
              className="bg-red-500 hover:bg-red-600 text-white font-semibold gap-1.5" data-testid="confirm-cancel-btn">
              {cancelling ? "Đang huỷ..." : "Xác nhận huỷ"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* E/F. Booking Modal */}
      <BookingModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        booking={editBooking}
        courts={courts}
        selectedDate={selectedDate}
        prefillCourtId={prefillCourtId}
        prefillTime={prefillTime}
        forceBookingType={forceBookingType}
        onSaved={invalidateBookings}
      />

      {/* PIN Dialog for delete */}
      <PinDialog open={deletePinOpen} onOpenChange={setDeletePinOpen}
        onConfirm={doDeleteBookingConfirmed}
        title="Xác nhận xóa đặt sân"
        description="Nhập mã PIN để xóa đặt sân này." />
    </div>
  );
}
